import React from 'react';
import {
  BRAND_SWATCHES,
  BRAND_SWATCH_KEYS,
  type BrandAccent,
} from '@/data/brandSwatches';

/**
 * BrandAccentPicker — responsive swatch grid for selecting a tenant brand
 * accent. Stateless / controlled; the parent owns `value` and persistence.
 *
 * Each swatch is a button rendering a colored circle (the swatch's
 * `previewHex`), the label below, and `aria-pressed` for the selected state.
 * Selected swatches get a 2px accent ring; the whole grid is single-select.
 */
export interface BrandAccentPickerProps {
  value: BrandAccent;
  onChange: (next: BrandAccent) => void;
  disabled?: boolean;
}

export const BrandAccentPicker: React.FC<BrandAccentPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  return (
    <div
      role="radiogroup"
      aria-label="Tenant brand accent"
      className="grid grid-cols-4 sm:grid-cols-8 gap-3"
    >
      {BRAND_SWATCH_KEYS.map((key) => {
        const swatch = BRAND_SWATCHES[key];
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            title={swatch.description}
            onClick={() => {
              if (!disabled) onChange(key);
            }}
            className={[
              'flex flex-col items-center justify-start gap-2',
              'min-h-11 min-w-11 p-2 rounded-md',
              'bg-bg-base border border-border-base',
              'transition-colors focus-visible:outline-none',
              'hover:bg-bg-elevated',
              selected
                ? 'border-accent ring-2 ring-accent ring-offset-2 ring-offset-bg-base'
                : '',
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span
              aria-hidden="true"
              className="block h-8 w-8 rounded-full border border-border-base"
              style={{ backgroundColor: swatch.previewHex }}
            />
            <span className="text-xs text-fg-secondary text-center leading-tight">
              {swatch.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default BrandAccentPicker;
